// THE "BETTER" MARKS OF A COMPARISON ANSWER (busy-ai-srv#41, Figma "⚖️ Compare", Better pill
// 40015163:56007).
//
// When ai-srv's planner reads a question as a comparison ("X vs Y", "X or Y for <goal>") it asks the
// model for ONE table with a column per option, and has it mark the clearly better cell of a row by
// ending it with ` [[better]]` — or ` [[better:Lighter]]` when one other short word says it better
// ("Cheaper", "Faster"). The page draws that mark as a small green pill after the cell's words; the
// mark itself must never be seen anywhere — not in the table, not in a line it strayed into, not in
// the copied answer, not in a saved extraction. This module is the one reading of the mark, with no
// imports, so the markdown renderer, the plain-text reader (lib/answerLineText.js) and the copy
// buttons can all use it without a cycle.

// ` [[better]]` / ` [[better:Word]]` (any case, spaces tolerated inside the brackets), with the
// spaces in front of it — never a newline, so stripping cannot join two lines. Global: a mark is
// stripped wherever it stands.
const MARK = /[ \t]*\[\[\s*better\s*(?::\s*([^\]]*?))?\s*\]\]/gi
// A label is ONE short word the model chose ("Lighter", "Cheaper", "Faster"). Anything longer or
// stranger is read as the plain "Better" rather than printed.
const LABEL = /^[\p{L}][\p{L}'-]{0,19}$/u

export const BETTER_LABEL = 'Better'

/** Does this text carry a Better mark anywhere? */
export const hasBetterMark = (s) => /\[\[\s*better\s*(?::[^\]]*)?\]\]/i.test(String(s ?? ''))

/**
 * One cell → `{ text, label }`: its words with every mark taken out, and the pill's label — '' when
 * the cell is not marked. The FIRST mark names the label; a mark standing inside the words (the model
 * wrote "£219 [[better]] · Currys") counts the same as one at the end — what the reader sees is the
 * same words with the pill after them.
 *
 * @param {string} cell a table cell, as written
 * @returns {{ text: string, label: string }}
 */
export function betterOf(cell) {
  const src = String(cell ?? '')
  // An unmarked cell is handed back exactly as written.
  if (!hasBetterMark(src)) return { text: src, label: '' }
  let label = ''
  const text = src.replace(MARK, (_m, word) => {
    if (!label) {
      const w = String(word ?? '').trim()
      label = LABEL.test(w) ? w.charAt(0).toUpperCase() + w.slice(1) : BETTER_LABEL
    }
    return ''
  }).replace(/[ \t]{2,}/g, ' ').trim()
  return { text, label }
}

/**
 * A table row's cells → `{ text, label }` each, with the TIE rule applied: the instruction allows at
 * most one marked cell per row, and a row where the model marked two or more is a row with no clear
 * winner — no pill on any of them (Figma: "Ties get no pill"). The marks are stripped either way.
 *
 * @param {string[]} cells
 * @returns {Array<{ text: string, label: string }>}
 */
export function betterRow(cells) {
  const read = (Array.isArray(cells) ? cells : []).map(betterOf)
  if (read.filter(c => c.label).length >= 2) return read.map(c => ({ text: c.text, label: '' }))
  return cheapestOnly(read)
}

// A PRICE row's pill goes on the lowest price, whatever the model wrote (prod 2026-10-27, Gemini:
// "$249 | $299 [[better:Cheaper]]" — the instruction says the lower price wins and the model still
// marked the dearer one). A row is a price row when its first cell says price/cost/fare or the pill
// says "Cheaper". Each option cell's FIRST amount is read ("£199 - £249" reads as 199); when the
// marked cell is not strictly the lowest of the amounts the row has, the pill is dropped — a wrong
// pill is worse than none. A row whose cells carry no readable amounts is left as the model wrote it.
const PRICE_ROW = /\b(price|prices|cost|costs|fare|fares|rrp|msrp)\b/i
const AMOUNT = /(\d{1,3}(?:[.,\s]\d{3})+|\d+)(?:[.,](\d{1,2}))?/
function amountOf(text) {
  const m = String(text ?? '').match(AMOUNT)
  if (!m) return null
  const whole = Number(m[1].replace(/[.,\s]/g, ''))
  return whole + (m[2] ? Number(`0.${m[2]}`) : 0)
}
function cheapestOnly(read) {
  const marked = read.findIndex((c, i) => i > 0 && c.label)
  if (marked < 0) return read
  const isPriceRow = PRICE_ROW.test(read[0]?.text || '') || /^cheaper$/i.test(read[marked].label)
  if (!isPriceRow) return read
  const amounts = read.map((c, i) => (i > 0 ? amountOf(c.text) : null))
  const mine = amounts[marked]
  const others = amounts.filter((a, i) => i > 0 && i !== marked && a != null)
  if (mine == null || !others.length) return read
  if (others.every(a => mine < a)) return read
  return read.map((c, i) => (i === marked ? { text: c.text, label: '' } : c))
}

/**
 * Any text → the same text with every Better mark gone. For everything that shows or hands over the
 * answer's raw markdown: the copy button, a line the mark strayed into, a saved extraction.
 * Text without a mark comes back unchanged, character for character.
 *
 * @param {string} md
 * @returns {string}
 */
export function stripCompareMarkers(md) {
  const src = String(md ?? '')
  return hasBetterMark(src) ? src.replace(MARK, '') : src
}
